import Seo from "../../components/seo";
import Layout from "../../components/layout";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceHero from "../../components/ServiceHero";
import ServiceCTA from "../../components/ServiceCTA";
import { CardColumn, HorizontalScroll, ContentImageSection } from "../../components/sections";
import { FiTrendingUp, FiCloud, FiCheckSquare, FiCpu, FiWifi, FiEdit3, FiTarget, FiZap, FiShield, FiStar } from 'react-icons/fi';
import bgImage from "../../images/img2.jpg";
import { usePageMetadata } from "../../hooks/usePageMetadata";

const ConsultingPage = () => {
  usePageMetadata({
    title: 'Consulting - Bercastle',
    description: 'Technology consulting and software assessment. Current technology stack evaluation, architecture review, and performance assessment.',
    pageType: 'service',
    content: {
      service: {
        name: "Consulting",
        tagline: "Strategic Technology Guidance",
        description: "Expert technology consulting and software assessment to optimize your tech stack and drive business growth.",
        url: "/services/consulting"
      },
      capabilities: [
        {
          name: "Technology Consulting & Software Assessment",
          description: "Current technology stack evaluation and optimization.",
          areas: ["Technology stack analysis", "Architecture review", "Performance evaluation", "Security assessment", "Scalability planning", "Cost optimization"]
        },
        {
          name: "Software Development Consulting",
          description: "End-to-end guidance on software projects from planning to deployment.",
          services: ["Requirements analysis", "Technology selection", "Architecture design", "Development methodology", "Team augmentation", "Project management"]
        },
        {
          name: "Cloud Strategy & Migration",
          description: "Plan and execute your cloud migration strategy with minimal disruption.",
          services: ["Cloud readiness assessment", "Migration planning", "AWS/Azure/GCP expertise", "Cost optimization", "Security & compliance"]
        },
        {
          name: "AI Strategy & Implementation",
          description: "Develop and execute your AI strategy to gain competitive advantage.",
          services: ["AI readiness assessment", "Use case identification", "Technology selection", "Implementation roadmap", "Team training"]
        }
      ],
      industries: ["Technology & SaaS", "Financial Services", "Healthcare", "E-commerce", "Manufacturing", "Media & Entertainment"],
      approach: ["Discovery", "Assessment", "Strategy", "Implementation", "Optimization"],
      value_propositions: [
        "Deep technical expertise",
        "Business-focused solutions",
        "Proven methodologies",
        "Vendor-agnostic advice",
        "Hands-on implementation",
        "Long-term partnerships"
      ],
      meta: {
        source_url: "https://bercastle.com/services/consulting",
        service_category: "Consulting"
      }
    }
  });
  const consultingServices = [
    {
      icon: <FiTrendingUp size={24} />,
      title: 'Technology Consulting & Software Assessment',
      description: 'Current technology stack evaluation and optimization, architecture review, performance assessment, and security audit.'
    },
    {
      icon: <FiCheckSquare size={24} />,
      title: 'Software Development Consulting & Implementation',
      description: 'Strategic product roadmap development, technology selection, implementation strategy, and team structure optimization.'
    }
  ];

  const competencies = [
    {
      icon: <FiTrendingUp size={24} />,
      title: 'Custom App Development',
      description: ''
    },
    {
      icon: <FiCloud size={24} />,
      title: 'Cloud Computing',
      description: ''
    },
    {
      icon: <FiCheckSquare size={24} />,
      title: 'Software Testing',
      description: ''
    },
    {
      icon: <FiCpu size={24} />,
      title: 'Startups & MVP Development',
      description: ''
    },
    {
      icon: <FiWifi size={24} />,
      title: 'IoT Technologies',
      description: ''
    },
    {
      icon: <FiEdit3 size={24} />,
      title: 'UI/UX Design',
      description: ''
    }
  ];

  const processSteps = [
    {
      icon: <FiCheckSquare size={24} />,
      title: 'BA & Requirements',
      description: 'Gather and analyze business requirements'
    },
    {
      icon: <FiTrendingUp size={24} />,
      title: 'Solution Design',
      description: 'Architect the optimal solution'
    },
    {
      icon: <FiCloud size={24} />,
      title: 'Development',
      description: 'Build with best practices'
    },
    {
      icon: <FiCheckSquare size={24} />,
      title: 'Testing',
      description: 'Ensure quality through rigorous testing'
    }
  ];

  const whyChoose = [
    {
      icon: <FiTarget size={24} />,
      title: 'Strategic Insight',
      description: 'Deep understanding of technology and business alignment'
    },
    {
      icon: <FiZap size={24} />,
      title: 'Rapid Results',
      description: 'Quick identification of issues and actionable solutions'
    },
    {
      icon: <FiShield size={24} />,
      title: 'Risk Mitigation',
      description: 'Proactive identification and management of technical risks'
    },
    {
      icon: <FiStar size={24} />,
      title: 'Innovation Focus',
      description: 'Latest technologies and best practices implementation'
    }
  ];

  return (
    <Layout classmain="consulting-page">
      <Seo
        title='IT Consulting Services - OnScript AI'
        metaDesciption='Expert IT consulting services to optimize your technology operations. From software assessment to implementation, we help you build, optimize, and scale.'
      />
      <Header />

      <ServiceHero 
        badge="IT Consulting"
        title="Expert Guidance for"
        titleHighlight="Technology Excellence"
        description="We provide consulting services that empower businesses to navigate complex technological landscapes with confidence."
        ctaLink="/contact"
      />

      {/* Services Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <ContentImageSection
            image={bgImage}
            imageAlt='Software Consulting'
          >
            <div>
              <h2 className='text-4xl md:text-5xl font-bold mb-6 text-slate-900'>Software Consulting Services</h2>
              <p className='text-xl text-slate-600 mb-8'>
                Comprehensive consulting services designed to assess your software infrastructure and streamline your operations
              </p>
              <div className='space-y-6'>
                <div>
                  <h3 className='text-xl font-bold mb-3 text-slate-900'>Technology Consulting & Software Assessment</h3>
                  <ul className='space-y-2 text-slate-600'>
                    <li className='flex items-start gap-2'>
                      <span className='text-brand-orange mt-1'>•</span>
                      <span>Current technology stack evaluation and optimization</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <span className='text-brand-orange mt-1'>•</span>
                      <span>Architecture review and recommendations</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <span className='text-brand-orange mt-1'>•</span>
                      <span>Performance and scalability assessment</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <span className='text-brand-orange mt-1'>•</span>
                      <span>Security audit and compliance review</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className='text-xl font-bold mb-3 text-slate-900'>Software Development Consulting & Implementation</h3>
                  <ul className='space-y-2 text-slate-600'>
                    <li className='flex items-start gap-2'>
                      <span className='text-brand-orange mt-1'>•</span>
                      <span>Strategic product roadmap development</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <span className='text-brand-orange mt-1'>•</span>
                      <span>Technology selection and vendor evaluation</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <span className='text-brand-orange mt-1'>•</span>
                      <span>Implementation strategy and execution</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <span className='text-brand-orange mt-1'>•</span>
                      <span>Team structure and process optimization</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </ContentImageSection>
        </div>
      </div>

      {/* Competency Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <HorizontalScroll
            title="Our IT Consulting Competency"
            description="Expert consulting team will assist you in building, optimizing, and scaling solutions that drive efficiency and growth"
            items={competencies}
            cardWidth={250}
          />
        </div>
      </div>

      {/* Process Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <CardColumn
            title="Our Software Consulting Process"
            description="A seamless flow from understanding business needs to delivering high-quality solutions"
            cards={processSteps}
            cols={4}
            className='w-full'
          />
        </div>
      </div>

      {/* Why Choose Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <CardColumn
            title="Why Choose OnScript AI?"
            description="Strategic tech partners who help you innovate, scale, and deliver with confidence"
            cards={whyChoose}
            cols={4}
            className='w-full'
          />
        </div>
      </div>

      <ServiceCTA 
        title="Need Expert IT Guidance?"
        description="Let's discuss how our consulting services can help optimize your technology operations and drive business growth."
        buttonText="Schedule a Consultation"
      />

      <Footer />
    </Layout>
  )
}

export default ConsultingPage
